/**
 * Secure URL Parameter Encryption/Decryption
 *
 * Uses AES encryption for actual security (not just encoding)
 * with a secret key derived from user session
 */

import CryptoJS from 'crypto-js';

// Base secret key from environment (256-bit hex)
// CRITICAL: Must be set in production environment variables
const BASE_SECRET_KEY = process.env.REACT_APP_ENCRYPTION_KEY;

// Validate secret key exists
if (!BASE_SECRET_KEY) {
  console.error('❌ CRITICAL: REACT_APP_ENCRYPTION_KEY not set in environment!');
  throw new Error('Encryption key not configured. Check .env file.');
}

// Validate key strength (should be at least 64 hex chars = 256 bits)
if (BASE_SECRET_KEY.length < 64) {
  console.warn('⚠️ WARNING: Encryption key is too short. Use 256-bit (64 hex chars) minimum.');
}

/**
 * Generate session-specific encryption key
 * Combines base key with user/quiz context for additional security
 *
 * @param {string} userId - Current user ID
 * @param {string} quizId - Current quiz ID
 * @returns {string} Session-specific encryption key
 */
export const generateSessionKey = (userId, quizId) => {
  // Combine base key with session context
  const sessionData = `${BASE_SECRET_KEY}_${userId}_${quizId}`;
  // Hash to create unique session key
  return CryptoJS.SHA256(sessionData).toString();
};

/**
 * Encrypt quiz state to secure URL parameter
 * Uses base secret key for encryption
 *
 * @param {Object} state - The state object to encrypt
 * @param {string} sessionKey - Optional session-specific key (for enhanced security)
 * @returns {string} Encrypted string safe for URL
 */
export const encodeQuizState = (state, sessionKey = null) => {
  try {
    const jsonString = JSON.stringify(state);
    const encryptionKey = sessionKey || BASE_SECRET_KEY;
    // AES encryption with session or base key
    const encrypted = CryptoJS.AES.encrypt(jsonString, encryptionKey).toString();
    // Make URL-safe by encoding
    return encodeURIComponent(encrypted);
  } catch (error) {
    console.error('Error encrypting quiz state:', error);
    return '';
  }
};

/**
 * Decrypt secure URL parameter back to quiz state
 * Uses base secret key for decryption
 *
 * @param {string} encrypted - The encrypted parameter
 * @param {string} sessionKey - Optional session-specific key (must match encryption key)
 * @returns {Object|null} Decrypted state object or null if invalid
 */
export const decodeQuizState = (encrypted, sessionKey = null) => {
  try {
    if (!encrypted) return null;
    // Decode from URL-safe format
    const decoded = decodeURIComponent(encrypted);
    const decryptionKey = sessionKey || BASE_SECRET_KEY;
    // AES decryption with session or base key
    const decrypted = CryptoJS.AES.decrypt(decoded, decryptionKey);
    const jsonString = decrypted.toString(CryptoJS.enc.Utf8);

    if (!jsonString) {
      console.warn('Decryption resulted in empty string - possible tampered data or wrong key');
      return null;
    }

    return JSON.parse(jsonString);
  } catch (error) {
    console.error('Error decrypting quiz state:', error);
    return null;
  }
};

/**
 * Encrypt array to secure parameter (for flags, etc.)
 * @param {Array} arr - Array to encrypt
 * @param {string} sessionKey - Optional session-specific key
 * @returns {string} Encrypted string
 */
export const encodeArray = (arr, sessionKey = null) => {
  try {
    if (!arr || arr.length === 0) return '';
    const jsonString = JSON.stringify(arr);
    const encryptionKey = sessionKey || BASE_SECRET_KEY;
    const encrypted = CryptoJS.AES.encrypt(jsonString, encryptionKey).toString();
    return encodeURIComponent(encrypted);
  } catch (error) {
    console.error('Error encrypting array:', error);
    return '';
  }
};

/**
 * Decrypt array from secure parameter
 * @param {string} encrypted - Encrypted string
 * @param {string} sessionKey - Optional session-specific key
 * @returns {Array} Decrypted array
 */
export const decodeArray = (encrypted, sessionKey = null) => {
  try {
    if (!encrypted) return [];
    const decoded = decodeURIComponent(encrypted);
    const decryptionKey = sessionKey || BASE_SECRET_KEY;
    const decrypted = CryptoJS.AES.decrypt(decoded, decryptionKey);
    const jsonString = decrypted.toString(CryptoJS.enc.Utf8);

    if (!jsonString) return [];

    return JSON.parse(jsonString);
  } catch (error) {
    console.error('Error decrypting array:', error);
    return [];
  }
};

